import { Menu, Bell, User, ChevronDown, LogOut, HelpCircle } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useProfileStore } from '@/stores/profileStore'

export interface TopBarProps {
  onMenuClick: () => void
  title?: string
}

export function TopBar({ onMenuClick, title }: TopBarProps) {
  const navigate = useNavigate()
  const { signOut } = useAuthStore()
  const { profile } = useProfileStore()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth/sign-in')
  }

  const firstName = profile?.first_name || 'User'
  const displayName = firstName.charAt(0).toUpperCase() + firstName.slice(1)

  return (
    <header className="h-16 bg-white border-b border-[#E3DED2] flex items-center justify-between px-4 lg:px-6">
      {/* Left section - Menu button (mobile) and title */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 text-[#6E7582] hover:text-[#0F1C2E] hover:bg-[#F6F3EC] rounded-lg transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6" />
        </button>

        {title && (
          <h1 className="text-lg font-semibold text-[#0F1C2E] hidden sm:block">
            {title}
          </h1>
        )}
      </div>

      {/* Right section - Notifications and user dropdown */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <Link
          to="/u/notifications"
          className="p-2 text-[#6E7582] hover:text-[#0F1C2E] hover:bg-[#F6F3EC] rounded-lg transition-colors relative"
          aria-label="Notifications"
        >
          <Bell className="w-5 h-5" />
          {/* Notification badge - show when there are unread notifications */}
          {/* <span className="absolute top-1 right-1 w-2 h-2 bg-[#C7796B] rounded-full" /> */}
        </Link>

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 p-2 text-[#6E7582] hover:text-[#0F1C2E] hover:bg-[#F6F3EC] rounded-lg transition-colors"
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
          >
            <div className="w-8 h-8 bg-[#D4AF5A] rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {firstName.charAt(0).toUpperCase()}
              </span>
            </div>
            <span className="hidden sm:block text-sm font-medium text-[#0F1C2E]">
              {displayName}
            </span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-[#E3DED2] py-1 z-50">
              {/* User info */}
              <div className="px-4 py-3 border-b border-[#E3DED2]">
                <p className="text-sm font-medium text-[#0F1C2E]">{displayName}</p>
                <p className="text-sm text-[#6E7582] truncate">{profile?.email}</p>
                {profile?.role && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-[#D4AF5A]/10 text-[#0F1C2E] rounded">
                    {profile.role.charAt(0).toUpperCase() + profile.role.slice(1)}
                  </span>
                )}
              </div>

              {/* Menu items */}
              <div className="py-1">
                <Link
                  to="/u/account"
                  onClick={() => setIsDropdownOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-[#0F1C2E] hover:bg-[#F6F3EC]"
                >
                  <User className="w-4 h-4" />
                  Account Settings
                </Link>
                <Link
                  to="/help"
                  onClick={() => setIsDropdownOpen(false)}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-[#0F1C2E] hover:bg-[#F6F3EC]"
                >
                  <HelpCircle className="w-4 h-4" />
                  Help & Support
                </Link>
              </div>

              {/* Sign out */}
              <div className="border-t border-[#E3DED2] py-1">
                <button
                  onClick={handleSignOut}
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm text-[#0F1C2E] hover:bg-[#F6F3EC]"
                >
                  <LogOut className="w-4 h-4" />
                  Log Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
